import React, { useCallback, useContext, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { ContextSession } from "../../services/ContextSession";
import {
  actualizarCargoMantenimiento,
  crearCargoMantenimiento,
  listarCargosMantenimiento,
} from "./enrolamientoApi";

const EMPTY_FORM = {
  descripcion: "",
  activo: true,
};

const EMPTY_FILTERS = {
  id: "",
  descripcion: "",
  estado: "TODOS",
  cargoPublico: "TODOS",
};

const EMPTY_PAGINATION = {
  page: 1,
  page_size: 25,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
};

export default function CargosEnrolamiento() {
  const session = useContext(ContextSession);
  const authToken = session?.getToken ? session.getToken() : "";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [incluirInactivos, setIncluirInactivos] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [modalOpen, setModalOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [pagination, setPagination] = useState(EMPTY_PAGINATION);

  const totalActivos = useMemo(
    () => items.filter((item) => Boolean(item?.Activo)).length,
    [items],
  );
  const filterId = String(filters.id || "").trim();
  const filterDescripcion = String(filters.descripcion || "").trim().toUpperCase();
  const filterEstado = String(filters.estado || "TODOS").trim().toUpperCase();
  const filterCargoPublico = String(filters.cargoPublico || "TODOS").trim().toUpperCase();
  const filteredItems = items;

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await listarCargosMantenimiento(authToken, {
        incluirInactivos,
        page: page + 1,
        pageSize: rowsPerPage,
        id: filterId,
        descripcion: filterDescripcion,
        estado: filterEstado,
        cargoPublico: filterCargoPublico,
      });
      const rows = Array.isArray(result?.items) ? result.items : [];
      const paginationData = result?.pagination || EMPTY_PAGINATION;
      setItems(rows);
      setPagination(paginationData);
      const serverPage = Math.max((Number(paginationData?.page) || 1) - 1, 0);
      if (serverPage !== page) {
        setPage(serverPage);
      }
    } catch (err) {
      setError(err.message || "No se pudo cargar el mantenimiento de cargos.");
    } finally {
      setLoading(false);
    }
  }, [
    authToken,
    incluirInactivos,
    page,
    rowsPerPage,
    filterId,
    filterDescripcion,
    filterEstado,
    filterCargoPublico,
  ]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  React.useEffect(() => {
    setPage(0);
  }, [filterId, filterDescripcion, filterEstado, filterCargoPublico]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
  };

  const openCreateModal = () => {
    resetForm();
    setError("");
    setSuccess("");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
  };

  const handleEdit = (row) => {
    setEditingId(Number(row?.CargoId));
    setForm({
      descripcion: String(row?.Descripcion || ""),
      activo: Boolean(row?.Activo),
    });
    setError("");
    setSuccess("");
    setModalOpen(true);
  };

  const handleSave = async () => {
    const descripcion = String(form.descripcion || "").trim().toUpperCase();
    if (!descripcion) {
      setError("Debe ingresar descripción.");
      return;
    }

    const payload = {
      Descripcion: descripcion,
      CargoPublico: true,
      Activo: Boolean(form.activo),
    };

    setSaving(true);
    setError("");
    setSuccess("");
    try {
      if (editingId !== null && editingId !== undefined) {
        await actualizarCargoMantenimiento(authToken, editingId, payload);
        setSuccess("Cargo actualizado correctamente.");
      } else {
        await crearCargoMantenimiento(authToken, payload);
        setSuccess("Cargo creado correctamente.");
      }
      resetForm();
      setModalOpen(false);
      await loadData();
    } catch (err) {
      setError(err.message || "No se pudo guardar el cargo.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={2} sx={{ p: 2.5, borderRadius: 3, border: "1px solid #dce7f7" }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#19498c" }}>
              Cargos - Mantenimiento
            </Typography>
            <Typography sx={{ color: "#5a6a80", fontSize: 14 }}>
              Registros: {pagination.total} | Activos en página: {totalActivos}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControlLabel
              control={
                <Checkbox
                  checked={incluirInactivos}
                  onChange={(event) => {
                    setIncluirInactivos(event.target.checked);
                    setPage(0);
                  }}
                />
              }
              label="Mostrar inactivos"
            />
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadData}
              disabled={loading || saving}
            >
              Recargar
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateModal}
              sx={{ backgroundColor: "#19498c" }}
            >
              Nuevo
            </Button>
          </Stack>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              label="Filtro ID"
              value={filters.id}
              onChange={(event) => setFilters((prev) => ({ ...prev, id: event.target.value }))}
              sx={{ width: { xs: "100%", md: 160 } }}
            />
            <TextField
              label="Filtro descripción"
              value={filters.descripcion}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, descripcion: event.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Filtro estado"
              select
              value={filters.estado}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, estado: event.target.value }))
              }
              sx={{ width: { xs: "100%", md: 190 } }}
            >
              <MenuItem value="TODOS">TODOS</MenuItem>
              <MenuItem value="ACTIVO">ACTIVO</MenuItem>
              <MenuItem value="INACTIVO">INACTIVO</MenuItem>
            </TextField>
            <TextField
              label="Filtro cargo público"
              select
              value={filters.cargoPublico}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, cargoPublico: event.target.value }))
              }
              sx={{ width: { xs: "100%", md: 220 } }}
            >
              <MenuItem value="TODOS">TODOS</MenuItem>
              <MenuItem value="SI">SI</MenuItem>
              <MenuItem value="NO">NO</MenuItem>
            </TextField>
            <Button variant="outlined" onClick={() => setFilters(EMPTY_FILTERS)}>
              Limpiar filtros
            </Button>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#19498c" }}>
                <TableCell sx={{ color: "#fff", fontWeight: 700 }}>ID</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 700 }}>Descripción</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 700 }}>Cargo Público</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 700 }}>Estado</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 700, width: 120 }}>
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && filteredItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Sin registros.
                  </TableCell>
                </TableRow>
              )}
              {filteredItems.map((row) => (
                <TableRow key={row.CargoId}>
                  <TableCell>{row.CargoId}</TableCell>
                  <TableCell>{row.Descripcion}</TableCell>
                  <TableCell>{row.CargoPublico ? "SI" : "NO"}</TableCell>
                  <TableCell>{row.Activo ? "ACTIVO" : "INACTIVO"}</TableCell>
                  <TableCell>
                    <IconButton color="primary" size="small" onClick={() => handleEdit(row)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={Number(pagination?.total || 0)}
            page={page}
            onPageChange={(_event, nextPage) => setPage(nextPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              const nextRowsPerPage = parseInt(event.target.value, 10) || 25;
              setRowsPerPage(nextRowsPerPage);
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Paper>
      </Paper>

      <Dialog open={modalOpen} onClose={closeModal} fullWidth maxWidth="sm">
        <DialogTitle>{editingId !== null ? "Editar Cargo" : "Nuevo Cargo"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label="Descripción"
              value={form.descripcion}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, descripcion: event.target.value.toUpperCase() }))
              }
              fullWidth
            />
            <Typography sx={{ fontWeight: 700, color: "#19498c" }}>
              Cargo Público: SI
            </Typography>
            <FormControlLabel
              label={form.activo ? "ACTIVO" : "INACTIVO"}
              control={
                <Switch
                  checked={Boolean(form.activo)}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, activo: event.target.checked }))
                  }
                />
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal} disabled={saving}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || loading}
            sx={{ backgroundColor: "#19498c" }}
          >
            {editingId !== null ? "Actualizar" : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
